import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { shopperService, marketerService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// Codice Fiscale = exactly 16 alphanumeric characters (Italian tax code).
const CF_PATTERN = /^[A-Z0-9]{16}$/i;
const CF_FORMAT_ERROR = 'Codice Fiscale must be exactly 16 alphanumeric characters.';

const formFromShopper = s => ({
  codiceFiscale: (s?.codiceFiscale ?? s?.taxCode ?? '').toString(),
  billingAddress: (
    s?.codiceFiscaleAddress ??
    s?.billingAddress ??
    s?.address ??
    ''
  ).toString(),
  billingProvince: (
    s?.codiceFiscaleProvince ??
    s?.billingProvince ??
    s?.province ??
    ''
  ).toString(),
  billingTown: (
    s?.codiceFiscaleCity ??
    s?.billingCity ??
    s?.city ??
    ''
  ).toString(),
  billingZipCode: (
    s?.codiceFiscaleZipCode ??
    s?.billingZipCode ??
    s?.zip ??
    ''
  ).toString(),
});

const ShopperEditCodiceFiscalePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const shopperFromState = location.state?.shopper;

  const [shopper, setShopper] = useState(shopperFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState(null);
  const [validateNotice, setValidateNotice] = useState(null);
  const [pendingAutoFill, setPendingAutoFill] = useState(null);
  const [validatedMeta, setValidatedMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [stripeSync, setStripeSync] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  const justVerifiedRef = useRef(false);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id })
    : ROUTES.SHOPPERS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.SHOPPERS);
      return;
    }
    navigate(detailHref, { state: shopper ? { shopper } : undefined });
  }, [detailHref, id, navigate, shopper]);

  const loadShopper = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await shopperService.getFormattedShopperDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load shopper details.');
        return;
      }
      setShopper(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        setForm(formFromShopper(data));
        setValidatedMeta({
          codiceFiscaleChecked: data.codiceFiscaleChecked === true,
          codiceFiscaleCheckRawResponse: data.codiceFiscaleCheckRawResponse || {},
        });
      } else {
        setForm(formFromShopper({}));
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load shopper:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load shopper. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadShopper();
  }, [loadShopper]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (stripeSync) setStripeSync(null);
  };

  const validate = currentForm => {
    if (!currentForm) return {};
    const requiredFields = [
      'codiceFiscale',
      'billingAddress',
      'billingProvince',
      'billingTown',
      'billingZipCode',
    ];
    const next = {};
    for (const key of requiredFields) {
      const value = String(currentForm[key] ?? '').trim();
      if (!value) next[key] = 'This field is required.';
    }
    // Format check overrides the generic "required" error for CF.
    const cf = String(currentForm.codiceFiscale ?? '').trim();
    if (cf && !CF_PATTERN.test(cf)) {
      next.codiceFiscale = CF_FORMAT_ERROR;
    }
    return next;
  };

  const otherAddressFieldsFilled = () => {
    if (!form) return false;
    const others = [
      'billingAddress',
      'billingTown',
      'billingProvince',
      'billingZipCode',
    ];
    return others.some(k => String(form[k] ?? '').trim() !== '');
  };

  const applyAutoFill = data => {
    setForm(prev => ({
      ...prev,
      // Keep the codice fiscale value the user typed — the validate endpoint
      // is the Partita Iva lookup; it echoes back `billingPartitaIva` but that
      // is just what we sent, not a CF-specific result. Only the address
      // suggestion is meaningful for the CF flow.
      billingAddress: data.billingAddress ?? prev.billingAddress,
      billingTown: data.billingTown ?? prev.billingTown,
      billingProvince: data.billingProvince ?? prev.billingProvince,
      billingZipCode: data.billingZipCode ?? prev.billingZipCode,
    }));
    setValidatedMeta({
      codiceFiscaleChecked: data.billingPartitaIvaChecked ?? true,
      codiceFiscaleCheckRawResponse: data.billingPartitaIvaRawResponse ?? {},
    });
    setFieldErrors({});
  };

  const handleValidate = async () => {
    if (!id || !form) return;
    const codiceFiscale = String(form.codiceFiscale ?? '').trim();
    if (!codiceFiscale) {
      setValidateError('Please enter a Codice Fiscale before validating.');
      return;
    }
    if (!CF_PATTERN.test(codiceFiscale)) {
      setValidateError(CF_FORMAT_ERROR);
      return;
    }
    setValidating(true);
    setValidateError(null);
    setValidateNotice(null);
    setPendingAutoFill(null);
    try {
      // BE reuses the Partita Iva validate endpoint for CF lookup
      // (release notes section 3). The :id is ignored internally, so we
      // pass the shopper id even though the URL is on the marketer service.
      const response = await marketerService.validatePartitaIva(
        id,
        codiceFiscale
      );
      if (response?.data) {
        setValidatedMeta({
          codiceFiscaleChecked: response.data.billingPartitaIvaChecked ?? true,
          codiceFiscaleCheckRawResponse:
            response.data.billingPartitaIvaRawResponse ?? {},
        });
        if (otherAddressFieldsFilled()) {
          setPendingAutoFill(response.data);
        } else {
          applyAutoFill(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to validate Codice Fiscale:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'InvalidRequest') {
        setValidateNotice(
          'No match found in the registry. You can still save the codice fiscale manually.'
        );
      } else {
        setValidateError(
          err instanceof ApiError
            ? err.message
            : 'Failed to validate Codice Fiscale. Please try again.'
        );
      }
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fill in all required fields.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    setStripeSync(null);
    try {
      const response = await shopperService.updateCodiceFiscale(id, {
        codiceFiscale: form.codiceFiscale,
        billingAddress: form.billingAddress,
        billingProvince: form.billingProvince,
        billingTown: form.billingTown,
        billingZipCode: form.billingZipCode,
        codiceFiscaleChecked: validatedMeta?.codiceFiscaleChecked ?? false,
        codiceFiscaleCheckRawResponse:
          validatedMeta?.codiceFiscaleCheckRawResponse ?? {},
      });
      const status = response?.data?.stripeCustomerSyncStatus || 'updated';
      const message =
        response?.data?.stripeCustomerSyncMessage ||
        'Codice Fiscale details saved.';
      setStripeSync({ status, message });
      setShopper(prev =>
        prev
          ? {
              ...prev,
              codiceFiscale: String(form.codiceFiscale).trim(),
              codiceFiscaleAddress: String(form.billingAddress).trim(),
              codiceFiscaleProvince: String(form.billingProvince).trim(),
              codiceFiscaleCity: String(form.billingTown).trim(),
              codiceFiscaleZipCode: String(form.billingZipCode).trim(),
              billingAddress: String(form.billingAddress).trim(),
              billingProvince: String(form.billingProvince).trim(),
              billingCity: String(form.billingTown).trim(),
              billingZipCode: String(form.billingZipCode).trim(),
              codiceFiscaleChecked:
                validatedMeta?.codiceFiscaleChecked ?? false,
              codiceFiscaleCheckRawResponse:
                validatedMeta?.codiceFiscaleCheckRawResponse ?? {},
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to update Codice Fiscale:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'FeatureInvalidPayload') {
        setError('Required fields are missing or invalid. Please review the values.');
      } else if (code === 'DatabaseNotFound') {
        setError('Shopper not found.');
      } else if (code === 'StripeError') {
        setError(
          err.message || 'Stripe customer sync failed. Please try again.'
        );
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save Codice Fiscale. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const shopperLabel =
    shopper?.fullName ||
    [shopper?.firstName, shopper?.lastName].filter(Boolean).join(' ') ||
    'Shopper';

  if (loading && !shopper) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const editable = revealed && !saving;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Codice Fiscale'
        subtitle="Edit the shopper's Codice Fiscale and make sure the data is correct"
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Shoppers', href: ROUTES.SHOPPERS },
          { name: shopperLabel, href: detailHref, state: { shopper } },
          { name: 'Edit Codice Fiscale' },
        ]}
      />

      {stripeSync && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>{stripeSync.message}</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      {pendingAutoFill && (
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-blue-800'>
            Validation successful. Replace the current billing data with the
            suggested values?
          </p>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPendingAutoFill(null)}
              className='px-3 py-1.5 border border-blue-300 rounded-md text-xs font-medium text-blue-700 bg-white hover:bg-blue-50'
            >
              Keep Current
            </button>
            <button
              type='button'
              onClick={() => {
                applyAutoFill(pendingAutoFill);
                setPendingAutoFill(null);
              }}
              className='px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700'
            >
              Replace
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {shopperLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Shopper ID: {shopper?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            <div>
              <label
                htmlFor='codiceFiscale'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Codice Fiscale <span className='text-red-600'>*</span>
              </label>
              <div className='flex gap-2'>
                <input
                  id='codiceFiscale'
                  type='text'
                  value={form?.codiceFiscale ?? ''}
                  onChange={e => {
                    handleChange('codiceFiscale')(e);
                    setValidateError(null);
                    setValidateNotice(null);
                    setPendingAutoFill(null);
                    setValidatedMeta(null);
                  }}
                  disabled={!editable}
                  className={`flex-1 ${inputClassFor('codiceFiscale')}`}
                  autoComplete='off'
                  aria-invalid={hasFieldError('codiceFiscale')}
                />
                <button
                  type='button'
                  onClick={handleValidate}
                  disabled={!editable || validating}
                  className='px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
                >
                  {validating ? 'Validating…' : 'Validate'}
                </button>
              </div>
              {hasFieldError('codiceFiscale') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.codiceFiscale}
                </p>
              )}
              {validateError && (
                <p className='mt-1 text-xs text-red-600'>{validateError}</p>
              )}
              {validateNotice && (
                <p className='mt-1 text-xs text-gray-500'>{validateNotice}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingAddress'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Address <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingAddress'
                type='text'
                value={form?.billingAddress ?? ''}
                onChange={handleChange('billingAddress')}
                disabled={!editable}
                className={inputClassFor('billingAddress')}
                autoComplete='street-address'
                aria-invalid={hasFieldError('billingAddress')}
              />
              {hasFieldError('billingAddress') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingAddress}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingProvince'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Province <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingProvince'
                type='text'
                value={form?.billingProvince ?? ''}
                onChange={handleChange('billingProvince')}
                disabled={!editable}
                className={inputClassFor('billingProvince')}
                autoComplete='address-level1'
                aria-invalid={hasFieldError('billingProvince')}
              />
              {hasFieldError('billingProvince') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingProvince}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingTown'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                City <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingTown'
                type='text'
                value={form?.billingTown ?? ''}
                onChange={handleChange('billingTown')}
                disabled={!editable}
                className={inputClassFor('billingTown')}
                autoComplete='address-level2'
                aria-invalid={hasFieldError('billingTown')}
              />
              {hasFieldError('billingTown') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingTown}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingZipCode'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                ZIP Code <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingZipCode'
                type='text'
                value={form?.billingZipCode ?? ''}
                onChange={handleChange('billingZipCode')}
                disabled={!editable}
                className={inputClassFor('billingZipCode')}
                autoComplete='postal-code'
                aria-invalid={hasFieldError('billingZipCode')}
              />
              {hasFieldError('billingZipCode') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingZipCode}
                </p>
              )}
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Codice Fiscale Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the shopper's codice fiscale
              details. Under GDPR, you are responsible for ensuring this data
              is accurate and up to date. This change will be logged with your
              user ID and a timestamp.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadShopper();
        }}
      />
    </div>
  );
};

export default ShopperEditCodiceFiscalePage;
